/**
 * Payment API
 *
 * Mock payment service for checkout and subscription functionality.
 */

import type {
  PaymentMethod,
  Cart,
  CheckoutSession,
  Transaction,
  Subscription,
  SubscriptionPlan,
  ParentVerification,
  InitiateParentVerificationRequest,
  ParentVerificationResult,
  User,
  AccountLinkage,
  ApiResponse,
  UUID,
} from '../types'
import { mockDb, delay } from './mockDb'
import { success, error } from './client'

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

// Mock subscription plans
const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'plan-free',
    name: 'Free',
    description: 'Get started with basic features',
    price: 0,
    interval: 'month',
    features: [
      'Access to free challenges',
      'Basic Track Records',
      'Join up to 3 communities',
      'Standard support',
    ],
  },
  {
    id: 'plan-explorer',
    name: 'Explorer',
    description: 'Perfect for active learners',
    price: 9.99,
    interval: 'month',
    features: [
      'All Free features',
      'Unlimited challenges',
      'Advanced Track Records',
      'Unlimited communities',
      'Priority support',
      'No ads',
    ],
    isPopular: true,
  },
  {
    id: 'plan-family',
    name: 'Family',
    description: 'Great for families with multiple kids',
    price: 19.99,
    interval: 'month',
    features: [
      'All Explorer features',
      'Up to 5 family members',
      'Parent dashboard',
      'Family activity reports',
      'Shared portfolios',
      'Premium support',
    ],
  },
]

/**
 * Payment API
 */
export const paymentApi = {
  /**
   * Get saved payment methods
   */
  async getPaymentMethods(): Promise<ApiResponse<PaymentMethod[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const methods = mockDb.findWhere<PaymentMethod>(
      'paymentMethods',
      m => m.userId === userId
    )

    return success(methods)
  },

  /**
   * Add a payment method (mock - simulates card tokenization)
   */
  async addPaymentMethod(
    type: PaymentMethod['type'],
    cardDetails?: {
      number: string
      expMonth: number
      expYear: number
      cvc: string
      name: string
    }
  ): Promise<ApiResponse<PaymentMethod>> {
    await delay(500) // Simulate processing

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Mock card validation
    if (type === 'card' && cardDetails) {
      // Simulate card decline for specific test numbers
      if (cardDetails.number.endsWith('0000')) {
        return error('Card declined')
      }
    }

    // Determine card brand from number
    const getBrand = (num: string): string => {
      if (num.startsWith('4')) return 'visa'
      if (num.startsWith('5')) return 'mastercard'
      if (num.startsWith('3')) return 'amex'
      return 'unknown'
    }

    // Set new card as default if it's the first one
    const existingMethods = mockDb.findWhere<PaymentMethod>(
      'paymentMethods',
      m => m.userId === userId
    )
    const isDefault = existingMethods.length === 0

    const method = mockDb.insert<PaymentMethod>('paymentMethods', {
      userId,
      type,
      last4: cardDetails ? cardDetails.number.slice(-4) : undefined,
      brand: cardDetails ? getBrand(cardDetails.number) : undefined,
      expiryMonth: cardDetails?.expMonth,
      expiryYear: cardDetails?.expYear,
      isDefault,
      billingName: cardDetails?.name,
    })

    return success(method)
  },

  /**
   * Remove a payment method
   */
  async removePaymentMethod(methodId: UUID): Promise<ApiResponse<{ success: boolean }>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const method = mockDb.findById<PaymentMethod>('paymentMethods', methodId)
    if (!method || method.userId !== userId) {
      return error('Payment method not found')
    }

    mockDb.delete('paymentMethods', methodId)

    return success({ success: true })
  },

  /**
   * Set default payment method
   */
  async setDefaultPaymentMethod(methodId: UUID): Promise<ApiResponse<PaymentMethod>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Unset current default
    const methods = mockDb.findWhere<PaymentMethod>(
      'paymentMethods',
      m => m.userId === userId
    )
    methods.forEach(m => {
      if (m.isDefault) {
        mockDb.update<PaymentMethod>('paymentMethods', m.id, { isDefault: false })
      }
    })

    // Set new default
    const updated = mockDb.update<PaymentMethod>('paymentMethods', methodId, {
      isDefault: true,
    })

    if (!updated) {
      return error('Payment method not found')
    }

    return success(updated)
  },

  /**
   * Create a checkout session
   */
  async createCheckoutSession(cart: Cart): Promise<ApiResponse<CheckoutSession>> {
    await delay(200)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const session = mockDb.insert<CheckoutSession>('checkoutSessions', {
      userId,
      cart,
      status: 'open',
      expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(), // 30 min expiry
    })

    return success(session)
  },

  /**
   * Process payment for checkout
   */
  async processPayment(
    sessionId: UUID,
    paymentMethodId: UUID
  ): Promise<ApiResponse<Transaction>> {
    await delay(1500) // Simulate payment processing

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const session = mockDb.findById<CheckoutSession>('checkoutSessions', sessionId)
    if (!session || session.userId !== userId) {
      return error('Checkout session not found')
    }

    if (session.status !== 'open') {
      return error('Checkout session is no longer valid')
    }

    // Mock payment success (90% success rate for demo)
    const isSuccessful = Math.random() > 0.1

    // Update session status
    mockDb.update<CheckoutSession>('checkoutSessions', sessionId, {
      status: isSuccessful ? 'completed' : 'open',
      paymentMethodId,
    })

    if (!isSuccessful) {
      return error('Payment declined. Please try again.')
    }

    // Create transaction record
    const transaction = mockDb.insert<Transaction>('transactions', {
      userId,
      amount: session.cart.total,
      currency: 'USD',
      status: 'succeeded',
      paymentMethodId,
      description: `Purchase of ${session.cart.items.length} item(s)`,
      items: session.cart.items,
      receiptUrl: `https://receipts.docurious.com/${Date.now()}`,
    })

    return success(transaction)
  },

  /**
   * Get transaction history
   */
  async getTransactions(): Promise<ApiResponse<Transaction[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const transactions = mockDb.findWhere<Transaction>(
      'transactions',
      t => t.userId === userId
    )

    // Sort by date descending
    transactions.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    return success(transactions)
  },

  /**
   * Get subscription plans
   */
  async getSubscriptionPlans(): Promise<ApiResponse<SubscriptionPlan[]>> {
    await delay(50)
    return success(SUBSCRIPTION_PLANS)
  },

  /**
   * Get user's subscription
   */
  async getSubscription(): Promise<ApiResponse<Subscription | null>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return success(null)
    }

    const subscriptions = mockDb.findWhere<Subscription>(
      'subscriptions',
      s => s.userId === userId && s.status === 'active'
    )

    return success(subscriptions[0] || null)
  },

  /**
   * Subscribe to a plan
   */
  async subscribe(
    planId: string,
    paymentMethodId: UUID
  ): Promise<ApiResponse<Subscription>> {
    await delay(1000)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId)
    if (!plan) {
      return error('Plan not found')
    }

    // Cancel existing subscription
    const existing = mockDb.findWhere<Subscription>(
      'subscriptions',
      s => s.userId === userId && s.status === 'active'
    )
    existing.forEach(s => {
      mockDb.update<Subscription>('subscriptions', s.id, { status: 'cancelled' })
    })

    const now = new Date()
    const periodEnd = new Date(now)
    periodEnd.setMonth(periodEnd.getMonth() + (plan.interval === 'year' ? 12 : 1))

    const subscription = mockDb.insert<Subscription>('subscriptions', {
      userId,
      planId,
      status: 'active',
      currentPeriodStart: now.toISOString(),
      currentPeriodEnd: periodEnd.toISOString(),
      cancelAtPeriodEnd: false,
      paymentMethodId,
    })

    return success(subscription)
  },

  /**
   * Cancel subscription
   */
  async cancelSubscription(atPeriodEnd = true): Promise<ApiResponse<Subscription>> {
    await delay(200)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const subscriptions = mockDb.findWhere<Subscription>(
      'subscriptions',
      s => s.userId === userId && s.status === 'active'
    )

    if (subscriptions.length === 0) {
      return error('No active subscription')
    }

    const updated = mockDb.update<Subscription>('subscriptions', subscriptions[0].id, {
      cancelAtPeriodEnd: atPeriodEnd,
      status: atPeriodEnd ? 'active' : 'cancelled',
    })

    if (!updated) {
      return error('Failed to cancel subscription')
    }

    return success(updated)
  },

  /**
   * Apply coupon to cart
   */
  async applyCoupon(cart: Cart, couponCode: string): Promise<ApiResponse<Cart>> {
    await delay(200)

    // Mock coupon validation
    const coupons: Record<string, number> = {
      WELCOME10: 0.1,
      SUMMER20: 0.2,
      FREESHIP: 0,
    }

    const discountRate = coupons[couponCode.toUpperCase()]
    if (discountRate === undefined) {
      return error('Invalid coupon code')
    }

    const discount = cart.subtotal * discountRate
    const newTotal = cart.subtotal - discount + cart.tax

    return success({
      ...cart,
      couponCode,
      discount,
      total: newTotal,
    })
  },

  // ============================================================================
  // COPPA Parent Verification ($1 Credit Card Verification)
  // ============================================================================

  /**
   * Initiate parent verification with $1 credit card charge
   * This is required for COPPA compliance when linking parent to under-13 child
   */
  async initiateParentVerification(
    request: InitiateParentVerificationRequest
  ): Promise<ApiResponse<ParentVerificationResult>> {
    await delay(1500) // Simulate payment processing

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Verify the child exists and is under 13
    const child = mockDb.findById<User>('users', request.childUserId)
    if (!child) {
      return error('Child account not found')
    }

    if (!child.isUnder13) {
      return error('Child account is not under 13 - verification not required')
    }

    // Verify payment method belongs to current user
    const paymentMethod = mockDb.findById<PaymentMethod>('paymentMethods', request.paymentMethodId)
    if (!paymentMethod || paymentMethod.userId !== userId) {
      return error('Payment method not found')
    }

    // Check for existing pending/verified verification
    const existingVerification = mockDb.findOne<ParentVerification>(
      'parentVerifications',
      (v) => v.parentUserId === userId && v.childUserId === request.childUserId
    )

    if (existingVerification) {
      if (existingVerification.status === 'verified') {
        return error('Verification already completed')
      }
      if (existingVerification.attemptCount >= 3) {
        return error('Maximum verification attempts exceeded. Please contact support.')
      }
    }

    // Simulate $1 charge (90% success rate for demo)
    const isSuccessful = Math.random() > 0.1

    if (!isSuccessful) {
      // Track failed attempt
      if (existingVerification) {
        mockDb.update<ParentVerification>('parentVerifications', existingVerification.id, {
          status: 'failed',
          attemptCount: existingVerification.attemptCount + 1,
          failureReason: 'Card declined',
        })
      } else {
        mockDb.insert<ParentVerification>('parentVerifications', {
          parentUserId: userId,
          childUserId: request.childUserId,
          verificationMethod: 'credit_card_1dollar',
          status: 'failed',
          chargeAmount: 100, // $1.00 in cents
          attemptCount: 1,
          failureReason: 'Card declined',
        })
      }

      return error('Payment declined. Please try again with a different card.')
    }

    // Create/update verification record
    const mockStripeChargeId = `ch_${Date.now()}_mock`
    let verification: ParentVerification

    if (existingVerification) {
      verification = mockDb.update<ParentVerification>('parentVerifications', existingVerification.id, {
        status: 'verified',
        stripeChargeId: mockStripeChargeId,
        verifiedAt: new Date().toISOString(),
        attemptCount: existingVerification.attemptCount + 1,
        failureReason: undefined,
      })!
    } else {
      verification = mockDb.insert<ParentVerification>('parentVerifications', {
        parentUserId: userId,
        childUserId: request.childUserId,
        verificationMethod: 'credit_card_1dollar',
        status: 'verified',
        stripeChargeId: mockStripeChargeId,
        chargeAmount: 100, // $1.00 in cents
        verifiedAt: new Date().toISOString(),
        attemptCount: 1,
      })
    }

    // Create or update account linkage
    const existingLinkage = mockDb.findOne<AccountLinkage>(
      'accountLinkages',
      (l) => l.parentUserId === userId && l.childUserId === request.childUserId
    )

    if (existingLinkage) {
      mockDb.update<AccountLinkage>('accountLinkages', existingLinkage.id, {
        status: 'active',
        consentGranted: true,
        consentGrantedAt: new Date().toISOString(),
      })
    } else {
      mockDb.insert<AccountLinkage>('accountLinkages', {
        parentUserId: userId,
        childUserId: request.childUserId,
        linkageType: 'parent_child',
        status: 'active',
        consentGranted: true,
        consentGrantedAt: new Date().toISOString(),
        reminderSentDay14: false,
        reminderSentDay28: false,
      })
    }

    // Update child account to Tier 2
    mockDb.update<User>('users', request.childUserId, {
      accountTier: 'tier_2_full',
      parentalConsentStatus: 'granted',
    })

    // Add child to parent's linked children
    const parent = mockDb.findById<User>('users', userId)
    if (parent) {
      const linkedChildIds = parent.linkedChildIds || []
      if (!linkedChildIds.includes(request.childUserId)) {
        mockDb.update<User>('users', userId, {
          linkedChildIds: [...linkedChildIds, request.childUserId],
        })
      }
    }

    // Schedule $1 refund (in real impl, this would be automatic)
    // For demo purposes, we'll simulate immediate refund
    const mockRefundId = `re_${Date.now()}_mock`
    mockDb.update<ParentVerification>('parentVerifications', verification.id, {
      status: 'refunded',
      stripeRefundId: mockRefundId,
      refundedAt: new Date().toISOString(),
    })

    return success({
      verificationId: verification.id,
      status: 'verified',
      message: 'Verification successful. The $1.00 charge has been refunded.',
    })
  },

  /**
   * Get verification status for a parent-child relationship
   */
  async getParentVerificationStatus(
    childUserId: UUID
  ): Promise<ApiResponse<ParentVerification | null>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const verification = mockDb.findOne<ParentVerification>(
      'parentVerifications',
      (v) => v.parentUserId === userId && v.childUserId === childUserId
    )

    return success(verification)
  },

  /**
   * Get all parent verifications for the current user
   */
  async getParentVerifications(): Promise<ApiResponse<ParentVerification[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const verifications = mockDb.findWhere<ParentVerification>(
      'parentVerifications',
      (v) => v.parentUserId === userId
    )

    return success(verifications)
  },
}
